import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { requireSupabase, isSupabaseConfigured } from '@/lib/supabase/client';
import type { ListingCondition, ListingStatus } from '@/types/domain';
import type { FuelType, Transmission } from '@/types/domain';

/**
 * Annonces de vente.
 *
 * Une annonce n'est visible qu'une fois validée par l'administration : c'est la
 * base qui l'impose, pas cet écran. Les requêtes ci-dessous n'ont donc aucun
 * filtre de statut à appliquer pour le public — elles ne verraient rien d'autre
 * de toute façon.
 */

export type Listing = {
  id: string;
  seller_id: string;
  status: ListingStatus;
  brand: string;
  model: string;
  year: number;
  condition: ListingCondition;
  mileage_km: number;
  price: number;
  currency: string;
  is_negotiable: boolean;
  transmission: Transmission;
  fuel: FuelType;
  seats: number;
  doors: number;
  color: string | null;
  city_id: string;
  description: string | null;
  contact_phone: string | null;
  show_phone: boolean;
  published_at: string | null;
  submitted_at: string | null;
  refusal_reason: string | null;
  views_count: number;
  created_at: string;
};

export type ListingSearchItem = Listing & {
  photo: string | null;
  city_name: string | null;
  seller_first_name: string | null;
};

export type ListingSearchResult = {
  total: number;
  limit: number;
  offset: number;
  items: ListingSearchItem[];
};

export type ListingCriteria = {
  q?: string;
  city?: string;
  brand?: string;
  fuel?: string;
  transmission?: string;
  condition?: string;
  price_min?: number;
  price_max?: number;
  year_min?: number;
  tri?: 'recent' | 'price_asc' | 'price_desc';
  limit?: number;
  offset?: number;
};

/** Catalogue public des voitures à vendre. */
export function useSearchListings(criteria: ListingCriteria) {
  return useQuery({
    queryKey: ['listings', 'search', criteria],
    enabled: isSupabaseConfigured,
    queryFn: async (): Promise<ListingSearchResult> => {
      const { data, error } = await requireSupabase().rpc('search_listings', {
        p_criteria: criteria as never,
      });
      if (error) throw new Error(error.message);
      return data as unknown as ListingSearchResult;
    },
  });
}

/** Une annonce et ses photos. */
export function useListing(id: string | undefined) {
  return useQuery({
    queryKey: ['listings', id],
    enabled: isSupabaseConfigured && Boolean(id),
    queryFn: async () => {
      const { data, error } = await requireSupabase()
        .from('car_listings')
        .select('*, car_listing_photos(*), cities(name, slug), ' +
          'seller:profiles!car_listings_seller_id_fkey(first_name, last_name)')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return data as unknown as
        | (Listing & {
            car_listing_photos: { id: string; public_url: string | null; is_primary: boolean }[];
            cities: { name: string; slug: string } | null;
            seller: { first_name: string | null; last_name: string | null } | null;
          })
        | null;
    },
  });
}

/** Les annonces du vendeur connecté, tous statuts confondus. */
export function useMyListings() {
  return useQuery({
    queryKey: ['listings', 'mine'],
    enabled: isSupabaseConfigured,
    queryFn: async () => {
      const { data, error } = await requireSupabase()
        .from('car_listings')
        .select('*, car_listing_photos(id, public_url, is_primary), cities(name)')
        .order('created_at', { ascending: false });
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as (Listing & {
        car_listing_photos: { id: string; public_url: string | null; is_primary: boolean }[];
        cities: { name: string } | null;
      })[];
    },
  });
}

export function useSellerStats() {
  return useQuery({
    queryKey: ['listings', 'stats'],
    enabled: isSupabaseConfigured,
    queryFn: async () => {
      const { data, error } = await requireSupabase().rpc('seller_dashboard_stats');
      if (error) throw new Error(error.message);
      return data as unknown as {
        total: number;
        published: number;
        pending: number;
        draft: number;
        refused: number;
        sold: number;
        views: number;
      };
    },
  });
}

export function useSaveListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: Partial<Listing> & { id?: string }) => {
      const client = requireSupabase();
      const { id, ...fields } = input;

      const query = id
        ? client.from('car_listings').update(fields as never).eq('id', id).select().single()
        : client.from('car_listings').insert(fields as never).select().single();

      const { data, error } = await query;
      if (error) throw new Error(error.message);
      return data as unknown as Listing;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['listings'] }),
  });
}

/**
 * Change l'état d'une annonce.
 *
 * Le vendeur ne peut viser que quatre états — la base refuse le reste. Passer
 * par une seule fonction évite d'éparpiller cette règle dans les écrans.
 */
export function useSetListingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      status: Extract<ListingStatus, 'draft' | 'pending' | 'sold' | 'archived'>;
    }) => {
      const { error } = await requireSupabase()
        .from('car_listings')
        .update({ status: input.status } as never)
        .eq('id', input.id);
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['listings'] }),
  });
}

/** Modération, côté administration. */
export function usePendingListings() {
  return useQuery({
    queryKey: ['listings', 'pending'],
    enabled: isSupabaseConfigured,
    queryFn: async () => {
      const { data, error } = await requireSupabase()
        .from('car_listings')
        .select('*, car_listing_photos(id, public_url, is_primary), cities(name), ' +
          'seller:profiles!car_listings_seller_id_fkey(first_name, last_name, email, phone)')
        .eq('status', 'pending')
        .order('submitted_at');
      if (error) throw new Error(error.message);
      return (data ?? []) as unknown as (Listing & {
        car_listing_photos: { id: string; public_url: string | null; is_primary: boolean }[];
        cities: { name: string } | null;
        seller: {
          first_name: string | null;
          last_name: string | null;
          email: string;
          phone: string | null;
        } | null;
      })[];
    },
  });
}

export function useReviewListing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; approve: boolean; reason?: string }) => {
      const { error } = await requireSupabase().rpc('review_listing', {
        p_listing_id: input.id,
        p_approve: input.approve,
        p_reason: input.reason ?? null,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['listings'] }),
  });
}

/** Ouvre — ou retrouve — la conversation avec le vendeur d'une annonce. */
export function useContactSeller() {
  return useMutation({
    mutationFn: async (listingId: string) => {
      const { data, error } = await requireSupabase().rpc('open_listing_conversation', {
        p_listing_id: listingId,
      });
      if (error) throw new Error(error.message);
      return data as unknown as { id: string };
    },
  });
}
